// 演练详情页：从 pageStore.detailRecordId 读 ID，调 /api/drill-records/:id
// 展示：基本信息 / 得分拆解 / 师傅评语 / 操作流水 / 老师傅时间轴对照
// 游客模式：mock 演练记录 + mock 专家/学生时间轴，可直接体验双轴对比

import { useEffect, useState } from 'react';
import { drillApi, mistakeApi, experienceApi, type DrillRecord, type DistilledExperience, type ExperienceRule } from '@/services/api';
import { usePageStore } from '@/stores/pageStore';
import { ArrowLeft, FileText, Award, Activity, CheckCircle2, XCircle, Printer, BookmarkPlus, Sparkles, Target, AlertCircle, Zap, Clock, PlayCircle, Play } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import TimelineCompare from '@/components/TimelineCompare';
import { DrillReplay } from '@/components/DrillReplay/DrillReplay';
import { getScenePack } from '@/templates';
import type { OperationRecord, DrillStep } from '@/types';
import type { ExpertTimeline } from '@/services/timeline';

const SCENE_LABEL: Record<string, string> = {
  fcc: '催化裂化',
  portcrane: '港口起重机',
  windturbine: '风力发电机',
  welding: '汽车焊装',
  cnc: '数控加工',
};

const GRADE_BADGE: Record<string, string> = {
  S: 'bg-yellow-400/25 text-yellow-400 border-yellow-400/50',
  A: 'bg-green-400/20 text-green-400 border-green-400/40',
  B: 'bg-blue-400/20 text-blue-400 border-blue-400/40',
  C: 'bg-orange-400/20 text-orange-400 border-orange-400/40',
  D: 'bg-red-400/20 text-red-400 border-red-400/40',
};

const DIFFICULTY_LABEL: Record<string, string> = {
  novice: '新手',
  standard: '标准',
  expert: '专家',
};

// =============================================================
// 游客模式 mock 数据
// =============================================================
const GUEST_RECORD_MAP: Record<string, DrillRecord> = {
  'gr1': { id: 'gr1', user_id: 'guest', scene_id: 'fcc', fault_id: 'fcc-1', fault_name: '再生器温度异常升高', start_time: Date.now() - 86400000 * 2, end_time: Date.now() - 86400000 * 2 + 285000, duration_sec: 285, score: 88, grade: 'A', difficulty: 'standard', score_breakdown: { dimensions: [{ name: '操作规范', score: 92, weight: 0.3 }, { name: '处置效率', score: 85, weight: 0.3 }, { name: '安全意识', score: 90, weight: 0.2 }, { name: '参数控制', score: 84, weight: 0.2 }], coachComment: '整体处置思路正确，先确认进料量再降温的操作节奏把握得当。不足之处在于降温幅度稍大，建议每次调节不超过5°C，并增加观察间隔。' }, operations: [{ action: '查看进料量', isCorrect: true, aiFeedback: '正确，第一时间检查进料量', timestamp: Date.now() - 86400000 * 2 }, { action: '稳定反应温度', isCorrect: true, aiFeedback: '操作及时', timestamp: Date.now() - 86400000 * 2 + 30000 }, { action: '降温-8°C', isCorrect: false, aiFeedback: '降幅过大，建议每次不超过5°C', timestamp: Date.now() - 86400000 * 2 + 60000 }, { action: '观察温度变化', isCorrect: true, timestamp: Date.now() - 86400000 * 2 + 90000 }, { action: '降温-6°C', isCorrect: false, aiFeedback: '降幅仍偏大', timestamp: Date.now() - 86400000 * 2 + 120000 }], created_at: Date.now() - 86400000 * 2 },
  'gr2': { id: 'gr2', user_id: 'guest', scene_id: 'portcrane', fault_id: 'pc-1', fault_name: '大车大风报警', start_time: Date.now() - 86400000 * 4, end_time: Date.now() - 86400000 * 4 + 195000, duration_sec: 195, score: 92, grade: 'S', difficulty: 'expert', score_breakdown: { dimensions: [{ name: '操作规范', score: 96, weight: 0.3 }, { name: '处置效率', score: 90, weight: 0.3 }, { name: '安全意识', score: 95, weight: 0.2 }, { name: '参数控制', score: 88, weight: 0.2 }], coachComment: '操作流程非常规范，制动→收臂→锚定三步顺序正确，节奏把控精准。继续保持。' }, operations: [{ action: '大车制动', isCorrect: true, aiFeedback: '第一时间制动，正确', timestamp: Date.now() - 86400000 * 4 }, { action: '收臂至安全位置', isCorrect: true, aiFeedback: '制动后收臂，顺序正确', timestamp: Date.now() - 86400000 * 4 + 20000 }, { action: '部署锚定装置', isCorrect: true, aiFeedback: '锚定到位', timestamp: Date.now() - 86400000 * 4 + 60000 }], created_at: Date.now() - 86400000 * 4 },
  'gr3': { id: 'gr3', user_id: 'guest', scene_id: 'fcc', fault_id: 'fcc-1', fault_name: '再生器温度异常升高', start_time: Date.now() - 86400000 * 5, end_time: Date.now() - 86400000 * 5 + 340000, duration_sec: 340, score: 72, grade: 'B', difficulty: 'standard', score_breakdown: { dimensions: [{ name: '操作规范', score: 78, weight: 0.3 }, { name: '处置效率', score: 65, weight: 0.3 }, { name: '安全意识', score: 80, weight: 0.2 }, { name: '参数控制', score: 68, weight: 0.2 }], coachComment: '降温时机偏晚，且未先检查进料量就直接降温，属于治标不治本。建议先排查根因。' }, operations: [{ action: '降温-10°C', isCorrect: false, aiFeedback: '未先检查进料量，直接降温', timestamp: Date.now() - 86400000 * 5 }, { action: '查看进料量', isCorrect: true, aiFeedback: '检查偏晚', timestamp: Date.now() - 86400000 * 5 + 60000 }, { action: '稳定反应温度', isCorrect: true, timestamp: Date.now() - 86400000 * 5 + 120000 }], created_at: Date.now() - 86400000 * 5 },
  'gr4': { id: 'gr4', user_id: 'guest', scene_id: 'windturbine', fault_id: 'wt-1', fault_name: '齿轮箱高温', start_time: Date.now() - 86400000 * 7, end_time: Date.now() - 86400000 * 7 + 220000, duration_sec: 220, score: 85, grade: 'A', difficulty: 'standard', score_breakdown: { dimensions: [{ name: '操作规范', score: 88, weight: 0.3 }, { name: '处置效率', score: 82, weight: 0.3 }, { name: '安全意识', score: 90, weight: 0.2 }, { name: '参数控制', score: 80, weight: 0.2 }], coachComment: '排查思路正确，先查油位再查冷却系统。但在油位正常时略有犹豫，建议加快判断节奏。' }, operations: [{ action: '检查润滑油油位', isCorrect: true, aiFeedback: '优先查油位，正确', timestamp: Date.now() - 86400000 * 7 }, { action: '检查冷却系统', isCorrect: true, aiFeedback: '排查顺序正确', timestamp: Date.now() - 86400000 * 7 + 60000 }, { action: '观察温度变化', isCorrect: true, timestamp: Date.now() - 86400000 * 7 + 120000 }], created_at: Date.now() - 86400000 * 7 },
  'gr5': { id: 'gr5', user_id: 'guest', scene_id: 'portcrane', fault_id: 'pc-1', fault_name: '大车大风报警', start_time: Date.now() - 86400000 * 9, end_time: Date.now() - 86400000 * 9 + 260000, duration_sec: 260, score: 68, grade: 'C', difficulty: 'novice', score_breakdown: { dimensions: [{ name: '操作规范', score: 60, weight: 0.3 }, { name: '处置效率', score: 70, weight: 0.3 }, { name: '安全意识', score: 65, weight: 0.2 }, { name: '参数控制', score: 80, weight: 0.2 }], coachComment: '操作顺序有误，先收臂再制动，存在安全隐患。需牢记"制动→收臂→锚定"的铁律。' }, operations: [{ action: '收臂至安全位置', isCorrect: false, aiFeedback: '应先制动再收臂', timestamp: Date.now() - 86400000 * 9 }, { action: '大车制动', isCorrect: true, aiFeedback: '制动偏晚', timestamp: Date.now() - 86400000 * 9 + 40000 }, { action: '部署锚定装置', isCorrect: true, timestamp: Date.now() - 86400000 * 9 + 90000 }], created_at: Date.now() - 86400000 * 9 },
  'gr6': { id: 'gr6', user_id: 'guest', scene_id: 'windturbine', fault_id: 'wt-1', fault_name: '齿轮箱高温', start_time: Date.now() - 86400000 * 12, end_time: Date.now() - 86400000 * 12 + 180000, duration_sec: 180, score: 95, grade: 'S', difficulty: 'expert', score_breakdown: { dimensions: [{ name: '操作规范', score: 98, weight: 0.3 }, { name: '处置效率', score: 95, weight: 0.3 }, { name: '安全意识', score: 92, weight: 0.2 }, { name: '参数控制', score: 94, weight: 0.2 }], coachComment: '处置完美，油位→冷却→判断一气呵成，未停机就解决了问题。' }, operations: [{ action: '检查润滑油油位', isCorrect: true, aiFeedback: '快速准确', timestamp: Date.now() - 86400000 * 12 }, { action: '检查冷却系统', isCorrect: true, timestamp: Date.now() - 86400000 * 12 + 30000 }, { action: '补油至正常范围', isCorrect: true, aiFeedback: '判断准确，油位确实偏低', timestamp: Date.now() - 86400000 * 12 + 60000 }], created_at: Date.now() - 86400000 * 12 },
  'gr7': { id: 'gr7', user_id: 'guest', scene_id: 'fcc', fault_id: 'fcc-1', fault_name: '再生器温度异常升高', start_time: Date.now() - 86400000 * 14, end_time: Date.now() - 86400000 * 14 + 310000, duration_sec: 310, score: 79, grade: 'B', difficulty: 'novice', score_breakdown: { dimensions: [{ name: '操作规范', score: 80, weight: 0.3 }, { name: '处置效率', score: 75, weight: 0.3 }, { name: '安全意识', score: 82, weight: 0.2 }, { name: '参数控制', score: 78, weight: 0.2 }], coachComment: '新手表现尚可，但操作节奏偏慢，建议多练习提高反应速度。' }, operations: [{ action: '查看进料量', isCorrect: true, timestamp: Date.now() - 86400000 * 14 }, { action: '稳定反应温度', isCorrect: true, timestamp: Date.now() - 86400000 * 14 + 60000 }, { action: '降温-5°C', isCorrect: true, aiFeedback: '降幅合适', timestamp: Date.now() - 86400000 * 14 + 120000 }], created_at: Date.now() - 86400000 * 14 },
};

// mock 专家经验（含时间轴）
const GUEST_EXPERT_MAP: Record<string, { expert: ExperienceRule; studentTimeline: ExpertTimeline }> = {
  'fcc': {
    expert: {
      id: 'ge1', scene_id: 'fcc', fault_id: 'fcc-1', fault_name: '再生器温度异常升高',
      title: '再生器超温的先兆判断与处置节奏',
      expert_name: '李建国', expert_title: '高级技师·28年',
      source_type: 'think_aloud', status: 'active',
      raw_transcript: '', created_at: Date.now() - 86400000 * 7, updated_at: Date.now() - 86400000 * 7,
      distilled: {
        master_insight: '再生器超温处置的核心在于"先稳后降"——先确认进料量是否变化以判断根因，再以小幅度逐步降温，避免催化剂结焦。',
        key_decisions: [
          { step: 1, action: '检查进料量是否变化', priority: 'critical', timing: '0-30秒', reasoning: '进料量变化会直接影响燃烧室热平衡' },
          { step: 2, action: '稳定反应温度', priority: 'critical', timing: '30-60秒', reasoning: '先稳住温度防止连锁反应' },
          { step: 3, action: '小幅度逐步降温', priority: 'recommended', timing: '60-180秒', reasoning: '每次不超过5°C，避免催化剂结焦' },
        ],
        param_thresholds: [
          { param: '再生器温度', danger_zone: '> 720°C', expert_action: '立即检查进料，小幅降温', normal_range: '680-710°C' },
        ],
        rhythm: '前30秒判断根因，30-60秒稳定系统，60-180秒逐步降温',
        intuition_rules: ['温度升+进料不变 = 燃烧室问题', '降温速度永远不超过5°C/次', '先稳后降，急降必结焦'],
        common_mistakes: [
          { mistake: '一看超温就猛降温', why_wrong: '温度骤降导致催化剂结焦', correct_alternative: '每次降温不超过5°C' },
        ],
      },
      timeline: {
        version: 1 as const, duration_sec: 180,
        events: [
          { t: 0, type: 'action' as const, target: 'feed_rate', targetLabel: '进料量', from: 100, to: 100, text: '检查进料量，确认无变化' },
          { t: 30, type: 'action' as const, target: 'reactor_temp', targetLabel: '反应温度', from: 705, to: 705, text: '稳定反应温度，观察趋势' },
          { t: 60, type: 'pause' as const, durationSec: 15, text: '观察温度变化趋势' },
          { t: 75, type: 'action' as const, target: 'cooling_valve', targetLabel: '冷却阀', from: 30, to: 35, text: '小幅降温-5°C' },
          { t: 105, type: 'pause' as const, durationSec: 15, text: '等待温度响应' },
          { t: 120, type: 'action' as const, target: 'cooling_valve', targetLabel: '冷却阀', from: 35, to: 40, text: '继续降温-5°C' },
          { t: 150, type: 'view' as const, target: 'regenerator', targetLabel: '再生器', text: '确认温度回落至正常区间' },
          { t: 170, type: 'note' as const, text: '处置完成，温度稳定在705°C' },
        ],
      },
    },
    studentTimeline: {
      version: 1 as const, duration_sec: 150,
      events: [
        { t: 0, type: 'action' as const, target: 'feed_rate', targetLabel: '进料量', from: 100, to: 100, text: '检查进料量' },
        { t: 30, type: 'action' as const, target: 'reactor_temp', targetLabel: '反应温度', from: 705, to: 705, text: '稳定反应温度' },
        { t: 60, type: 'action' as const, target: 'cooling_valve', targetLabel: '冷却阀', from: 30, to: 38, text: '降温-8°C（降幅过大）' },
        { t: 90, type: 'pause' as const, durationSec: 10, text: '观察温度变化' },
        { t: 100, type: 'action' as const, target: 'cooling_valve', targetLabel: '冷却阀', from: 38, to: 44, text: '继续降温-6°C（仍偏大）' },
        { t: 130, type: 'view' as const, target: 'regenerator', targetLabel: '再生器', text: '查看温度' },
      ],
    },
  },
  'portcrane': {
    expert: {
      id: 'ge2', scene_id: 'portcrane', fault_id: 'pc-1', fault_name: '大车大风报警',
      title: '港机大风工况的制动与锚定流程',
      expert_name: '张德海', expert_title: '高级技师·22年',
      source_type: 'interview', status: 'active',
      raw_transcript: '', created_at: Date.now() - 86400000 * 3, updated_at: Date.now() - 86400000 * 3,
      distilled: {
        master_insight: '大风工况处置的铁律是"制动→收臂→锚定"三步顺序，不可颠倒。',
        key_decisions: [
          { step: 1, action: '大车制动', priority: 'critical', timing: '0-10秒', reasoning: '先锁死大车防止溜车' },
          { step: 2, action: '收臂至安全位置', priority: 'critical', timing: '10-60秒', reasoning: '制动后再收臂' },
          { step: 3, action: '部署锚定装置', priority: 'recommended', timing: '60-120秒', reasoning: '锚定是最后保险' },
        ],
        param_thresholds: [{ param: '风速', danger_zone: '> 20m/s', expert_action: '制动+收臂+锚定', normal_range: '< 15m/s' }],
        rhythm: '10秒制动，50秒收臂，60秒锚定',
        intuition_rules: ['大风先制动，别管别的', '风速没降下来别操作', '锚定不能省'],
        common_mistakes: [{ mistake: '先收臂再制动', why_wrong: '臂受风力可能失控', correct_alternative: '严格按制动→收臂→锚定' }],
      },
      timeline: {
        version: 1 as const, duration_sec: 120,
        events: [
          { t: 0, type: 'action' as const, target: 'brake', targetLabel: '大车制动器', from: 0, to: 1, text: '大车制动' },
          { t: 10, type: 'action' as const, target: 'boom', targetLabel: '臂架', from: 80, to: 0, text: '收臂至安全位置' },
          { t: 60, type: 'action' as const, target: 'anchor', targetLabel: '锚定装置', from: 0, to: 1, text: '部署锚定' },
          { t: 90, type: 'pause' as const, durationSec: 10, text: '等待风速降低' },
          { t: 100, type: 'note' as const, text: '风速降至12m/s，处置完成' },
        ],
      },
    },
    studentTimeline: {
      version: 1 as const, duration_sec: 100,
      events: [
        { t: 0, type: 'action' as const, target: 'boom', targetLabel: '臂架', from: 80, to: 0, text: '先收臂（顺序错误！）' },
        { t: 40, type: 'action' as const, target: 'brake', targetLabel: '大车制动器', from: 0, to: 1, text: '大车制动（偏晚）' },
        { t: 70, type: 'action' as const, target: 'anchor', targetLabel: '锚定装置', from: 0, to: 1, text: '部署锚定' },
      ],
    },
  },
  'windturbine': {
    expert: {
      id: 'ge3', scene_id: 'windturbine', fault_id: 'wt-1', fault_name: '齿轮箱高温',
      title: '风机齿轮箱高温的现场判断与应急处理',
      expert_name: '王秀兰', expert_title: '技师·15年',
      source_type: 'observation', status: 'active',
      raw_transcript: '', created_at: Date.now() - 86400000, updated_at: Date.now() - 86400000,
      distilled: {
        master_insight: '齿轮箱高温处置遵循"油位→冷却→齿轮"三级排查法，能不停机就不停机。',
        key_decisions: [
          { step: 1, action: '检查润滑油油位', priority: 'critical', timing: '0-60秒', reasoning: '油位低最常见' },
          { step: 2, action: '检查冷却系统', priority: 'critical', timing: '60-120秒', reasoning: '冷却故障是第二原因' },
          { step: 3, action: '停机检查齿轮磨损', priority: 'recommended', timing: '120秒+', reasoning: '最后才停机' },
        ],
        param_thresholds: [{ param: '齿轮箱温度', danger_zone: '> 85°C', expert_action: '排查+停机', normal_range: '60-75°C' }],
        rhythm: '1分钟查油位，1分钟查冷却，最后才停机',
        intuition_rules: ['油位低就补油', '冷却看风扇和泵', '能不停机就不停机'],
        common_mistakes: [{ mistake: '高温直接停机', why_wrong: '未排查根因', correct_alternative: '先排查油位和冷却' }],
      },
      timeline: {
        version: 1 as const, duration_sec: 120,
        events: [
          { t: 0, type: 'action' as const, target: 'oil_level', targetLabel: '润滑油油位', from: 50, to: 50, text: '检查油位' },
          { t: 60, type: 'action' as const, target: 'cooling_sys', targetLabel: '冷却系统', from: 1, to: 1, text: '检查冷却系统运行状态' },
          { t: 90, type: 'pause' as const, durationSec: 15, text: '观察温度变化' },
          { t: 105, type: 'note' as const, text: '油位偏低，补油后温度下降' },
        ],
      },
    },
    studentTimeline: {
      version: 1 as const, duration_sec: 120,
      events: [
        { t: 0, type: 'action' as const, target: 'oil_level', targetLabel: '润滑油油位', from: 50, to: 50, text: '检查油位' },
        { t: 60, type: 'action' as const, target: 'cooling_sys', targetLabel: '冷却系统', from: 1, to: 1, text: '检查冷却系统' },
        { t: 90, type: 'pause' as const, durationSec: 10, text: '观察温度' },
      ],
    },
  },
};

export const HistoryDetailPage: React.FC = () => {
  const detailRecordId = usePageStore((s) => s.detailRecordId);
  const setPage = usePageStore((s) => s.setPage);
  const user = useAuthStore((s) => s.user);
  const isGuest = user?.id === 'guest';
  const [record, setRecord] = useState<DrillRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [expertExp, setExpertExp] = useState<DistilledExperience | null>(null);
  // P1-4: 专家时间轴 + 学生时间轴 + 是否展开对照面板
  const [expertExpFull, setExpertExpFull] = useState<ExperienceRule | null>(null);
  const [studentTimeline, setStudentTimeline] = useState<ExpertTimeline | null>(null);
  const [showTimelineCompare, setShowTimelineCompare] = useState(false);
  const [timelineLoading, setTimelineLoading] = useState(false);
  // 演练回放：是否展开回放组件
  const [showReplay, setShowReplay] = useState(false);

  useEffect(() => {
    if (!detailRecordId) return;
    setLoading(true);
    if (isGuest) {
      // 游客模式：直接用 mock 数据
      const mockRecord = GUEST_RECORD_MAP[detailRecordId];
      if (mockRecord) {
        setRecord(mockRecord);
        const expertData = GUEST_EXPERT_MAP[mockRecord.scene_id];
        if (expertData) {
          setExpertExp(expertData.expert.distilled);
          setExpertExpFull(expertData.expert);
          setStudentTimeline(expertData.studentTimeline);
        }
      }
      setLoading(false);
      return;
    }
    drillApi
      .detail(detailRecordId)
      .then((r) => {
        setRecord(r.record);
        // 加载专家经验 + 时间轴（一并取出，时间轴可能为 null）
        if (r.record?.scene_id && r.record?.fault_id) {
          experienceApi.getByFault(r.record.scene_id, r.record.fault_id)
            .then(({ experience }) => {
              setExpertExp(experience?.distilled ?? null);
              setExpertExpFull(experience ?? null);
            })
            .catch(() => {
              setExpertExp(null);
              setExpertExpFull(null);
            });
        }
      })
      .finally(() => setLoading(false));
  }, [detailRecordId, isGuest]);

  /** 学生侧 — 点开"看老师傅时间轴"才懒加载自己的时间轴 */
  const handleToggleTimeline = async () => {
    if (showTimelineCompare) {
      setShowTimelineCompare(false);
      return;
    }
    if (!detailRecordId) return;
    if (isGuest) {
      // 游客模式：studentTimeline 已在 useEffect 中设置
      setShowTimelineCompare(true);
      return;
    }
    if (!studentTimeline) {
      setTimelineLoading(true);
      try {
        const r = await experienceApi.studentTimeline(detailRecordId);
        setStudentTimeline(r.timeline);
      } catch {
        // 拉不到则保留 null，对照组件会提示
      } finally {
        setTimelineLoading(false);
      }
    }
    setShowTimelineCompare(true);
  };

  /** 导出 PDF：用 window.print()，配合下面 .printable-report 区域的打印样式 */
  const handleExport = () => {
    // 改标题，浏览器"另存为 PDF"的默认文件名会跟这个走
    const oldTitle = document.title;
    const safeName = record
      ? `实训报告_${SCENE_LABEL[record.scene_id] ?? record.scene_id}_${formatTime(record.created_at).replace(/[ :]/g, '_')}`
      : '实训报告';
    document.title = safeName;
    setTimeout(() => {
      window.print();
      setTimeout(() => {
        document.title = oldTitle;
      }, 300);
    }, 80);
  };

  if (!detailRecordId) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-text-muted">
        没有指定记录 ID
        <button onClick={() => setPage('history')} className="ml-3 text-primary">
          返回列表
        </button>
      </div>
    );
  }

  if (loading || !record) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-text-muted">
        加载中...
      </div>
    );
  }

  const sb: any = record.score_breakdown ?? {};
  const operations: any[] = Array.isArray(record.operations) ? record.operations : [];

  // 得分维度（FCC 的 breakdown 有 dimensions: 数组）
  const dimensions: any[] = Array.isArray(sb.dimensions) ? sb.dimensions : [];
  const coachComment: string = sb.coachComment || '';

  // —— 演练回放所需数据 ——
  // 学员操作序列：API DrillRecord.operations 实际结构同 OperationRecord
  const replayOperations: OperationRecord[] = operations as OperationRecord[];
  const replayStartTime: number =
    typeof record.start_time === 'number' ? record.start_time : record.created_at;
  const replayEndTime: number =
    typeof record.end_time === 'number' ? record.end_time : replayStartTime + (record.duration_sec ?? 0) * 1000;
  // 标准处方：从场景包 faults 中按 fault_id 命中，取 correct 步骤
  const replayCorrectSteps: DrillStep[] = (() => {
    const pack = getScenePack(record.scene_id);
    const fault = pack?.faults.find((f) => f.id === record.fault_id);
    return fault?.steps.filter((s) => s.correct) ?? [];
  })();

  return (
    <div className="h-full overflow-y-auto bg-bg-primary text-text-primary">
      <div className="max-w-4xl mx-auto p-6 space-y-4 printable-report">
        {/* 仅打印时显示的报告页头 */}
        <div className="print-only border-b-2 border-black pb-3 mb-3">
          <div className="text-xl font-bold text-black">匠魂实训引擎 · 实训报告</div>
          <div className="text-xs text-gray-600 mt-1">
            学员：{user?.display_name ?? '-'}（@{user?.username ?? '-'}）
            {user?.student_no ? ` · 学号 ${user.student_no}` : ''}
            <span className="ml-3">导出时间：{formatTime(Date.now())}</span>
          </div>
        </div>

        {/* 顶部 */}
        <div className="flex items-center gap-3 no-print">
          <button
            onClick={() => setPage(isGuest && user?.role === 'teacher' ? 'dashboard' : 'history')}
            className="p-2 text-text-muted hover:text-text-primary"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="text-xs text-text-muted">
              {SCENE_LABEL[record.scene_id] ?? record.scene_id} ·{' '}
              {record.difficulty ? DIFFICULTY_LABEL[record.difficulty] : '-'} ·{' '}
              {formatTime(record.created_at)}
              {isGuest && <span className="ml-2 text-cyan-400">游客演示</span>}
            </div>
            <h1 className="text-xl font-bold mt-0.5">{record.fault_name}</h1>
          </div>
          {!isGuest && (
          <button
            onClick={async () => {
              if (!record) return;
              try {
                await mistakeApi.add({
                  scene_id: record.scene_id,
                  fault_id: record.fault_id,
                  fault_name: record.fault_name,
                  score: record.score,
                  grade: record.grade,
                });
                alert('已加入错题本，前往「错题本」可重练此故障');
              } catch (e: any) {
                alert('加入错题本失败：' + (e?.message ?? ''));
              }
            }}
            title="把本次演练加入错题本（即使评分较高也可手动标记）"
            className="px-3 py-2 text-sm bg-bg-tertiary hover:bg-bg-secondary border border-border text-text-primary rounded-lg flex items-center gap-2"
          >
            <BookmarkPlus className="w-4 h-4" />
            标错题
          </button>
          )}
          <button
            onClick={handleExport}
            title="导出为 PDF（浏览器打印 → 另存为 PDF）"
            className="px-3 py-2 text-sm bg-warning hover:bg-warning/90 text-white rounded-lg flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            导出 PDF
          </button>
          <div
            className={`px-4 py-2 rounded-lg border-2 text-center ${
              GRADE_BADGE[record.grade] ?? ''
            }`}
          >
            <div className="text-3xl font-bold">{record.grade}</div>
            <div className="text-xs">{record.score} 分</div>
          </div>
        </div>

        {/* 打印版的标题+成绩（替代上面那行） */}
        <div className="print-only">
          <div className="text-base font-bold text-black">
            场景：{SCENE_LABEL[record.scene_id] ?? record.scene_id}
            {record.difficulty ? ` · 难度：${DIFFICULTY_LABEL[record.difficulty]}` : ''}
          </div>
          <div className="text-sm text-black mt-1">
            故障：{record.fault_name}（编号 {record.fault_id}）
          </div>
          <div className="text-sm text-black mt-2">
            最终评定：<strong>{record.grade} 级</strong> · <strong>{record.score} 分</strong>
            <span className="ml-3">演练时间：{formatTime(record.created_at)}</span>
          </div>
        </div>

        {/* 概览四宫格 */}
        <div className="grid grid-cols-4 gap-3">
          <InfoCell label="用时" value={formatDuration(record.duration_sec)} />
          <InfoCell label="操作次数" value={`${operations.length} 步`} />
          <InfoCell
            label="正确率"
            value={
              operations.length === 0
                ? '-'
                : `${Math.round(
                    (operations.filter((o) => o.isCorrect).length / operations.length) * 100,
                  )}%`
            }
          />
          <InfoCell label="故障 ID" value={record.fault_id} />
        </div>

        {/* 得分拆解 */}
        {dimensions.length > 0 && (
          <div className="bg-bg-secondary border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Award className="w-4 h-4 text-primary" />
              得分拆解
            </h3>
            <div className="space-y-2">
              {dimensions.map((d, i) => (
                <DimensionRow key={i} dim={d} />
              ))}
            </div>
          </div>
        )}

        {/* 师傅讲评 */}
        {coachComment && (
          <div className="bg-bg-secondary border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-warning" />
              师傅讲评
            </h3>
            <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">
              {coachComment}
            </p>
          </div>
        )}

        {/* 专家做法对比 */}
        {expertExp && (
          <div className="bg-bg-secondary border border-purple-500/30 rounded-lg p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-purple-400" />
                老师傅经验对比
              </h3>
              {/* P1-4: 时间轴对照按钮 — 仅当后端返回了时间轴才显示 */}
              {expertExpFull?.timeline && expertExpFull.timeline.events.length > 0 && (
                <button
                  type="button"
                  onClick={handleToggleTimeline}
                  disabled={timelineLoading}
                  className="inline-flex items-center gap-1 rounded-md border border-purple-500/40 bg-purple-500/10 px-2.5 py-1 text-xs text-purple-300 hover:bg-purple-500/20 disabled:opacity-50"
                >
                  <PlayCircle className="w-3.5 h-3.5" />
                  {timelineLoading
                    ? '加载中…'
                    : showTimelineCompare
                    ? '收起时间轴对照'
                    : '🎬 查看老师傅时间轴对照'}
                </button>
              )}
            </div>

            {/* P1-4: 双轨时间轴对照面板（学生 vs 专家） */}
            {showTimelineCompare && expertExpFull?.timeline && (
              <div className="mb-4">
                <TimelineCompare
                  expert={expertExpFull.timeline}
                  student={studentTimeline}
                  expertName={expertExpFull.expert_name}
                  expertTitle={expertExpFull.expert_title}
                  onClose={() => setShowTimelineCompare(false)}
                />
              </div>
            )}

            <div className="space-y-4">
              {/* 核心经验 */}
              {expertExp.master_insight && (
                <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                  <p className="text-sm text-purple-300 italic">"{expertExp.master_insight}"</p>
                </div>
              )}

              {/* 专家关键操作 */}
              {expertExp.key_decisions?.length > 0 && (
                <div>
                  <div className="text-xs text-text-muted font-medium mb-2 flex items-center gap-1">
                    <Target className="w-3.5 h-3.5" />
                    专家关键操作
                  </div>
                  <div className="space-y-1.5">
                    {expertExp.key_decisions.map((d, i) => (
                      <div key={i} className="flex items-start gap-2 p-2 bg-bg-tertiary rounded">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ${d.priority === 'critical' ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {d.priority === 'critical' ? '关键' : '建议'}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-text-primary">
                            第{d.step}步：{d.action}
                          </div>
                          <div className="text-[10px] text-text-muted mt-0.5">
                            时机：{d.timing} · 原因：{d.reasoning}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 操作节奏 */}
              {expertExp.rhythm && (
                <div className="flex items-start gap-2 p-2 bg-bg-tertiary rounded">
                  <Clock className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-xs text-text-muted">操作节奏：</span>
                    <span className="text-xs text-text-secondary">{expertExp.rhythm}</span>
                  </div>
                </div>
              )}

              {/* 新手常见错误 */}
              {expertExp.common_mistakes?.length > 0 && (
                <div>
                  <div className="text-xs text-text-muted font-medium mb-2 flex items-center gap-1">
                    <AlertCircle className="w-3.5 h-3.5" />
                    新手常见错误（对照你的操作）
                  </div>
                  <div className="space-y-1.5">
                    {expertExp.common_mistakes.map((m, i) => (
                      <div key={i} className="p-2 bg-red-500/5 border border-red-500/20 rounded">
                        <div className="text-xs text-red-400">{m.mistake}</div>
                        <div className="text-[10px] text-text-muted mt-0.5">原因：{m.why_wrong}</div>
                        <div className="text-[10px] text-green-400 mt-0.5">正确做法：{m.correct_alternative}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 经验直觉 */}
              {expertExp.intuition_rules?.length > 0 && (
                <div>
                  <div className="text-xs text-text-muted font-medium mb-2 flex items-center gap-1">
                    <Zap className="w-3.5 h-3.5" />
                    经验直觉
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {expertExp.intuition_rules.map((r, i) => (
                      <span key={i} className="text-xs px-2 py-1 bg-yellow-500/10 border border-yellow-500/20 text-yellow-400/80 rounded">
                        {r}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 演练回放 */}
        {replayOperations.length > 0 && (
          <div className="bg-bg-secondary border border-border rounded-lg overflow-hidden no-print">
            <button
              type="button"
              onClick={() => setShowReplay((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-bg-tertiary/40 transition-colors"
            >
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Play className="w-4 h-4 text-primary" />
                演练回放
                <span className="text-xs text-text-muted font-normal">
                  · 按时间轴动态复盘操作过程并对照标准处方
                </span>
              </div>
              <span className="text-xs text-text-muted">
                {showReplay ? '收起 ▲' : '▶ 回放演练'}
              </span>
            </button>
            {showReplay && (
              <div className="px-4 pb-4 pt-1">
                <DrillReplay
                  operations={replayOperations}
                  correctSteps={replayCorrectSteps}
                  startTime={replayStartTime}
                  endTime={replayEndTime}
                  faultName={record.fault_name}
                />
              </div>
            )}
          </div>
        )}

        {/* 操作流水 */}
        <div className="bg-bg-secondary border border-border rounded-lg p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
            <Activity className="w-4 h-4 text-cyan-400" />
            操作流水 ({operations.length} 条)
          </h3>
          {operations.length === 0 ? (
            <p className="text-xs text-text-muted text-center py-3">无操作记录</p>
          ) : (
            <div className="space-y-1.5">
              {operations.map((op, i) => (
                <OperationItem key={i} op={op} index={i + 1} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoCell: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="bg-bg-secondary border border-border rounded p-3">
    <div className="text-xs text-text-muted">{label}</div>
    <div className="text-base font-medium mt-0.5 truncate">{value}</div>
  </div>
);

const DimensionRow: React.FC<{ dim: any }> = ({ dim }) => {
  const score = typeof dim.score === 'number' ? dim.score : 0;
  const weight = typeof dim.weight === 'number' ? dim.weight : 0;
  const pct = Math.min(100, Math.max(0, score));
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <div>
          <span className="font-medium">{dim.name ?? dim.key ?? '指标'}</span>
          {weight > 0 && (
            <span className="ml-1.5 text-text-muted">权重 {Math.round(weight * 100)}%</span>
          )}
        </div>
        <span className="font-mono font-medium">{Math.round(score)} / 100</span>
      </div>
      <div className="h-1.5 bg-bg-tertiary rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-primary to-cyan-400 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      {dim.note && (
        <div className="text-[11px] text-text-muted mt-1">{dim.note}</div>
      )}
    </div>
  );
};

const OperationItem: React.FC<{ op: any; index: number }> = ({ op, index }) => {
  const correct = op.isCorrect === true;
  return (
    <div className="flex items-start gap-2 text-xs px-2 py-1.5 bg-bg-tertiary rounded">
      <span className="text-text-muted shrink-0">#{index}</span>
      {correct ? (
        <CheckCircle2 className="w-3.5 h-3.5 text-green-400 mt-0.5 shrink-0" />
      ) : (
        <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 shrink-0" />
      )}
      <div className="flex-1 min-w-0">
        <div className="truncate">{op.action ?? '未知操作'}</div>
        {op.aiFeedback && (
          <div className="text-[11px] text-text-muted mt-0.5">{op.aiFeedback}</div>
        )}
      </div>
      {op.timestamp && (
        <span className="text-[10px] text-text-muted shrink-0">
          {new Date(op.timestamp).toLocaleTimeString()}
        </span>
      )}
    </div>
  );
};

function formatTime(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m${String(s).padStart(2, '0')}s`;
}
